import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Button, Table, Modal, Form, Alert, Badge, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import Pagination from '../../components/common/Pagination';
import { useAuth } from '../../contexts/AuthContext';

const VisionStatementsManagement = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userPermissions = useMemo(() => {
    if (!user) return [];
    return Array.isArray(user.permissions) ? user.permissions : [];
  }, [user]);

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (!requiredPermission) return false;
      if (userPermissions.includes('*')) return true;
      return userPermissions.includes(requiredPermission);
    },
    [userPermissions]
  );

  const canViewVision = useMemo(
    () => hasPermission('vision_statements') || hasPermission('vision_statements.view'),
    [hasPermission]
  );

  const canCreateVision = useMemo(
    () => hasPermission('vision_statements') || hasPermission('vision_statements.create'),
    [hasPermission]
  );

  const canUpdateVision = useMemo(
    () => hasPermission('vision_statements') || hasPermission('vision_statements.update'),
    [hasPermission]
  );

  const canDeleteVision = useMemo(
    () => hasPermission('vision_statements') || hasPermission('vision_statements.delete'),
    [hasPermission]
  );
  
  // State management
  const [showModal, setShowModal] = useState(false);
  const [editingVision, setEditingVision] = useState(null);
  const [formData, setFormData] = useState({
    grade_id: 0,
    vision: ''
  });
  const [errors, setErrors] = useState({});
  
  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filterGrade, itemsPerPage]);

  // Fetch vision statements
  const { data: visionsResponse, isLoading, error: visionsError } = useQuery({
    queryKey: ['staff_vision_statements', currentPage, itemsPerPage, debouncedSearchTerm, filterGrade],
    queryFn: async () => {
      const offset = (currentPage - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString()
      });
      
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (filterGrade) params.append('grade_id', filterGrade);
      
      const response = await apiService.get(`/api/staff/vision_statements?${params}`);
      return response;
    },
    enabled: canViewVision
  });

  const visions = useMemo(() => {
    if (Array.isArray(visionsResponse)) return visionsResponse;
    if (Array.isArray(visionsResponse?.data)) return visionsResponse.data;
    if (Array.isArray(visionsResponse?.data?.data)) return visionsResponse.data.data;
    return [];
  }, [visionsResponse]);

  const totalItems = useMemo(() => {
    if (typeof visionsResponse?.total === 'number') return visionsResponse.total;
    if (typeof visionsResponse?.data?.total === 'number') return visionsResponse.data.total;
    if (typeof visionsResponse?.data?.count === 'number') return visionsResponse.data.count;
    return visions.length;
  }, [visionsResponse, visions.length]);

  const totalPages = Math.ceil(totalItems / itemsPerPage || 1);

  // Fetch grades for dropdown
  const { data: gradesData } = useQuery({
    queryKey: ['staff_grades_dropdown'],
    queryFn: async () => {
      const response = await apiService.get('/api/staff/grades_dropdown');
      // Response structure: { status: 'success', data: [...] }
      return Array.isArray(response.data) ? response.data : 
             Array.isArray(response) ? response : [];
    }
  });

  const grades = React.useMemo(() => {
    if (Array.isArray(gradesData)) {
      return gradesData;
    }
    if (Array.isArray(gradesData?.data)) {
      return gradesData.data;
    }
    if (Array.isArray(gradesData?.data?.data)) {
      return gradesData.data.data;
    }
    return [];
  }, [gradesData]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => apiService.post('/api/staff/vision_statements', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff_vision_statements']);
      toast.success('Vision statement created successfully!');
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create vision statement');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => apiService.put(`/api/staff/vision_statements/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff_vision_statements']);
      toast.success('Vision statement updated successfully!');
      handleCloseModal();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update vision statement');
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => apiService.delete(`/api/staff/vision_statements/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff_vision_statements']);
      toast.success('Vision statement deleted successfully!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete vision statement');
    }
  });

  // Handlers
  const handleShowModal = (vision = null) => {
    if (vision && !canUpdateVision) {
      toast.error('You do not have permission to update vision statements.');
      return;
    }

    if (!vision && !canCreateVision) {
      toast.error('You do not have permission to create vision statements.');
      return;
    }

    if (vision) {
      setEditingVision(vision);
      setFormData({
        grade_id: vision.grade_id || 0,
        vision: vision.vision || ''
      });
    } else {
      setEditingVision(null);
      setFormData({
        grade_id: 0,
        vision: ''
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVision(null);
    setFormData({ grade_id: 0, vision: '' });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.vision || formData.vision.trim() === '') {
      newErrors.vision = 'Vision is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (editingVision && !canUpdateVision) {
      toast.error('You do not have permission to update vision statements.');
      return;
    }

    if (!editingVision && !canCreateVision) {
      toast.error('You do not have permission to create vision statements.');
      return;
    }

    if (!validateForm()) {
      return;
    }

    const gradeId = formData.grade_id === '' || formData.grade_id === null ? 0 : parseInt(formData.grade_id, 10);
    const submitData = {
      grade_id: Number.isNaN(gradeId) ? 0 : gradeId,
      vision: formData.vision.trim()
    };

    if (editingVision) {
      updateMutation.mutate({ id: editingVision.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDelete = (vision) => {
    if (!canDeleteVision) {
      toast.error('You do not have permission to delete vision statements.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete this vision statement by ${vision.staff_name}?`)) {
      deleteMutation.mutate(vision.id);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterGrade('');
    setCurrentPage(1);
  };

  if (!canViewVision) {
    return (
      <Alert variant="warning" className="shadow-sm">
        <div className="d-flex align-items-center">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <div>
            <h6 className="mb-1">Permission Required</h6>
            <p className="mb-0">You do not have permission to view vision statements. Please contact the administrator if you believe this is incorrect.</p>
          </div>
        </div>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">
            <i className="bi bi-lightbulb me-2"></i>
            Vision Statements Management
          </h4>
          <p className="text-muted mb-0">Manage vision statements for staff and grades</p>
        </div>
        <Button 
          variant="primary" 
          onClick={() => handleShowModal()}
          disabled={!canCreateVision}
          title={canCreateVision ? 'Add a new vision statement' : 'You do not have permission to add vision statements'}
        >
          <i className="bi bi-plus-circle me-2"></i>
          Add Vision Statement
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={6} className="mb-2 mb-md-0">
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search vision statements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4} className="mb-2 mb-md-0">
              <Form.Select
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
              >
                <option value="">All Grades</option>
                <option value="0">Common</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button 
                variant="outline-secondary" 
                className="w-100"
                onClick={handleClearFilters}
              >
                <i className="bi bi-x-circle me-2"></i>
                Clear
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Vision Statements Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body>
          {visionsError ? (
            <Alert variant="danger" className="text-center">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {visionsError?.response?.data?.message || 'Failed to load vision statements.'}
            </Alert>
          ) : isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : visions.length === 0 ? (
            <Alert variant="secondary" className="text-center">
              <i className="bi bi-inbox display-4 text-muted d-block mb-3"></i>
              No vision statements found. Create your first vision statement!
            </Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '5%' }}>ID</th>
                      <th style={{ width: '15%' }}>Staff</th>
                      <th style={{ width: '10%' }}>Grade</th>
                      <th style={{ width: '50%' }}>Vision</th>
                      <th style={{ width: '10%' }}>Created</th>
                      <th style={{ width: '10%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visions.map((vision) => (
                      <tr key={vision.id}>
                        <td>{vision.id}</td>
                        <td>
                          <div>
                            <div className="fw-medium">{vision.staff_name}</div>
                            <small className="text-muted">{vision.staff_email}</small>
                          </div>
                        </td>
                        <td>
                          {vision.grade_id == 0 ? (
                            <Badge bg="primary">Common</Badge>
                          ) : (
                            <Badge bg="info">{vision.grade_name}</Badge>
                          )}
                        </td>
                        <td>
                          <div 
                            style={{ 
                              maxHeight: '60px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {vision.vision}
                          </div>
                        </td>
                        <td>
                          <small>{new Date(vision.created_date).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <Button
                              variant="outline-primary"
                              onClick={() => handleShowModal(vision)}
                              title={canUpdateVision ? 'Edit' : 'You do not have permission to edit vision statements'}
                              disabled={!canUpdateVision}
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              onClick={() => handleDelete(vision)}
                              title={canDeleteVision ? 'Delete' : 'You do not have permission to delete vision statements'}
                              disabled={!canDeleteVision}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalItems > 0 && (
                <div className="mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton className="bg-primary text-white" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
          <Modal.Title>
            <i className="bi bi-lightbulb me-2"></i>
            {editingVision ? 'Edit Vision Statement' : 'Add Vision Statement'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-muted">
                    <i className="bi bi-person me-1"></i>Staff
                  </Form.Label>
                  <Form.Control value={user?.name || 'You'} disabled />
                  <Form.Text className="text-muted">Only your own vision statements can be managed here.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <div className="form-floating mb-3">
                  <Form.Select
                    id="grade_id"
                    value={formData.grade_id}
                    onChange={(e) => handleInputChange('grade_id', e.target.value)}
                  >
                    <option value="0">Common (All Grades)</option>
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Label htmlFor="grade_id">
                    <i className="bi bi-bookmark me-1"></i>Grade
                  </Form.Label>
                </div>
              </Col>
            </Row>

            <div className="form-floating mb-3">
              <Form.Control
                as="textarea"
                id="vision"
                placeholder="Enter vision statement"
                value={formData.vision}
                onChange={(e) => handleInputChange('vision', e.target.value)}
                isInvalid={!!errors.vision}
                style={{ height: '150px' }}
              />
              <Form.Label htmlFor="vision">
                <i className="bi bi-lightbulb me-1"></i>Vision Statement *
              </Form.Label>
              <Form.Control.Feedback type="invalid">
                {errors.vision}
              </Form.Control.Feedback>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <i className="bi bi-x-circle me-2"></i>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={createMutation.isLoading || updateMutation.isLoading}
          >
            {(createMutation.isLoading || updateMutation.isLoading) ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                {editingVision ? 'Update' : 'Create'}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VisionStatementsManagement;

